import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import PolicySection from './components/PolicySection';
import SEO from '../../components/SEO';
import TableOfContents from './components/TableOfContents';

const sections = [
  { id: 'information-we-collect', title: 'Information We Collect' },
  { id: 'how-we-use', title: 'How We Use Your Information' },
  { id: 'data-storage', title: 'Data Storage & Security' },
  { id: 'third-party', title: 'Third-Party Services' },
  { id: 'cookies', title: 'Cookies & Tracking' },
  { id: 'marketing', title: 'Marketing Communications' },
  { id: 'your-rights', title: 'Your Rights' },
  { id: 'data-retention', title: 'Data Retention' },
  { id: 'gdpr', title: 'GDPR Compliance' },
  { id: 'contact', title: 'Contact Us' },
];

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ fontFamily: "'DM Sans', sans-serif", backgroundColor: '#F8F9FC' }}>
      <SEO
        title="Privacy Policy — ADU Flow"
        description="Read ADU Flow's privacy policy to understand how we collect, use, and protect your personal data when using our ADU construction CRM platform."
        canonical="/privacy-policy"
        keywords="ADU Flow privacy policy, data protection, GDPR compliance, CRM privacy, construction CRM security"
      />
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800&display=swap');`}</style>

      {/* Header */}
      <header style={{ background: '#0D2137', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 text-sm font-medium transition-colors hover:text-orange-400"
                style={{ color: 'rgba(255,255,255,0.65)' }}
              >
                <ArrowLeft size={16} />
                Back to Home
              </button>
            </div>
            <img
              src="/assets/images/adu_flow_logo.png"
              alt="ADU Flow"
              className="h-8 w-auto object-contain cursor-pointer"
              style={{ filter: 'brightness(0) invert(1)' }}
              onClick={() => navigate('/')}
            />
          </div>
        </div>
      </header>

      {/* Hero */}
      <div style={{ background: 'linear-gradient(135deg, #0D2137 0%, #1B3A5C 100%)' }} className="py-14 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold mb-5"
            style={{ background: 'rgba(232,115,42,0.15)', color: '#f08040', border: '1px solid rgba(232,115,42,0.3)' }}
          >
            <Shield size={14} />
            Legal Document
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3" style={{ letterSpacing: '-0.02em' }}>
            Privacy Policy
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.55)' }} className="text-sm">
            Last updated: March 4, 2026 &nbsp;·&nbsp; Effective: March 4, 2026
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-4 gap-10">
          {/* Sidebar TOC */}
          <aside className="hidden lg:block lg:col-span-1">
            <TableOfContents sections={sections} />
          </aside>

          {/* Main Content */}
          <main className="lg:col-span-3">
            <div className="rounded-2xl bg-white p-8 sm:p-10" style={{ border: '1px solid rgba(13,33,55,0.08)', boxShadow: '0 4px 24px rgba(13,33,55,0.06)' }}>
              <p className="text-sm mb-8 leading-relaxed" style={{ color: '#6B7280' }}>
                ADU Flow ("we", "us", or "our") is committed to protecting your personal information. This Privacy Policy explains how we collect, use, store, and protect data when you use our CRM platform at <strong style={{ color: '#0D2137' }}>aduflow.net</strong>. By using ADU Flow, you agree to the practices described below.
              </p>

              <PolicySection id="information-we-collect" title="1. Information We Collect" defaultOpen={true}>
                <p className="text-sm">We collect the following categories of information when you use ADU Flow:</p>
                <ul className="text-sm space-y-2 mt-2 ml-4 list-disc">
                  <li><strong>Account Information:</strong> Name, email address, company name, phone number, and password when you register.</li>
                  <li><strong>Business Data:</strong> Customer records, project details, permit information, contractor profiles, and team member data you enter into the platform.</li>
                  <li><strong>Usage Data:</strong> Pages visited, features used, session duration, browser type, IP address, and device information collected automatically.</li>
                  <li><strong>Payment Information:</strong> Billing details processed securely through Stripe. We do not store full credit card numbers on our servers.</li>
                  <li><strong>Communications:</strong> Support requests, feedback, and any correspondence you send to us.</li>
                </ul>
              </PolicySection>

              <PolicySection id="how-we-use" title="2. How We Use Your Information">
                <p className="text-sm">We use your information for the following purposes:</p>
                <ul className="text-sm space-y-2 mt-2 ml-4 list-disc">
                  <li>Providing, maintaining, and improving the ADU Flow platform and its features.</li>
                  <li>Processing subscription payments and managing your account billing.</li>
                  <li>Sending transactional emails such as account confirmations, password resets, and billing receipts.</li>
                  <li>Responding to support requests and customer service inquiries.</li>
                  <li>Analyzing usage patterns to improve platform performance and user experience.</li>
                  <li>Complying with legal obligations and enforcing our Terms of Use.</li>
                  <li>Sending marketing communications where you have provided consent (you may opt out at any time).</li>
                </ul>
              </PolicySection>

              <PolicySection id="data-storage" title="3. Data Storage & Security">
                <p className="text-sm">Your data is stored securely using Supabase, a PostgreSQL-based cloud database platform with enterprise-grade security:</p>
                <ul className="text-sm space-y-2 mt-2 ml-4 list-disc">
                  <li><strong>Encryption:</strong> All data is encrypted in transit using TLS 1.2+ and at rest using AES-256 encryption.</li>
                  <li><strong>Access Controls:</strong> Row-level security (RLS) policies ensure each organization can only access their own data.</li>
                  <li><strong>Data Centers:</strong> Data is stored in secure, SOC 2 compliant data centers.</li>
                  <li><strong>Backups:</strong> Automated daily backups with point-in-time recovery capabilities.</li>
                  <li><strong>Authentication:</strong> Secure JWT-based authentication with token expiration and refresh mechanisms.</li>
                </ul>
                <p className="text-sm mt-3">While we implement industry-standard security measures, no system is 100% impenetrable. We encourage you to use strong passwords and keep your credentials confidential.</p>
              </PolicySection>

              <PolicySection id="third-party" title="4. Third-Party Services">
                <p className="text-sm">ADU Flow integrates with the following third-party services, each governed by their own privacy policies:</p>
                <ul className="text-sm space-y-2 mt-2 ml-4 list-disc">
                  <li><strong>Supabase:</strong> Database hosting and authentication. <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#E8732A' }}>Privacy Policy</a></li>
                  <li><strong>Stripe:</strong> Payment processing for subscriptions. <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#E8732A' }}>Privacy Policy</a></li>
                  <li><strong>Vercel / Hosting Provider:</strong> Application hosting and content delivery.</li>
                </ul>
                <p className="text-sm mt-3">We do not sell, rent, or trade your personal information to third parties for their marketing purposes.</p>
              </PolicySection>

              <PolicySection id="cookies" title="5. Cookies & Tracking">
                <p className="text-sm">ADU Flow uses cookies and similar technologies to:</p>
                <ul className="text-sm space-y-2 mt-2 ml-4 list-disc">
                  <li><strong>Session Cookies:</strong> Maintain your authenticated session while using the platform.</li>
                  <li><strong>Preference Cookies:</strong> Remember your settings and preferences across visits.</li>
                  <li><strong>Analytics Cookies:</strong> Understand how users interact with the platform to improve functionality.</li>
                </ul>
                <p className="text-sm mt-3">You can control cookie settings through your browser. Disabling certain cookies may affect platform functionality. We do not use cookies for cross-site advertising tracking.</p>
              </PolicySection>

              <PolicySection id="marketing" title="6. Marketing Communications">
                <p className="text-sm">We may send you marketing emails about new features, product updates, and relevant industry content. You can:</p>
                <ul className="text-sm space-y-2 mt-2 ml-4 list-disc">
                  <li>Opt out at any time using the unsubscribe link in any marketing email.</li>
                  <li>Update your communication preferences in your account settings.</li>
                  <li>Contact us directly at <a href="mailto:privacy@aduflow.net" style={{ color: '#E8732A' }}>privacy@aduflow.net</a> to be removed from all marketing lists.</li>
                </ul>
                <p className="text-sm mt-3">Transactional emails (billing receipts, password resets, account alerts) cannot be opted out of as they are necessary for service delivery.</p>
              </PolicySection>

              <PolicySection id="your-rights" title="7. Your Rights">
                <p className="text-sm">You have the following rights regarding your personal data:</p>
                <ul className="text-sm space-y-2 mt-2 ml-4 list-disc">
                  <li><strong>Access:</strong> Request a copy of all personal data we hold about you.</li>
                  <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data.</li>
                  <li><strong>Deletion:</strong> Request deletion of your account and associated personal data (subject to legal retention requirements).</li>
                  <li><strong>Portability:</strong> Request your data in a machine-readable format for transfer to another service.</li>
                  <li><strong>Restriction:</strong> Request that we limit how we process your data in certain circumstances.</li>
                  <li><strong>Objection:</strong> Object to processing of your data for direct marketing purposes.</li>
                </ul>
                <p className="text-sm mt-3">To exercise any of these rights, contact us at <a href="mailto:privacy@aduflow.net" style={{ color: '#E8732A' }}>privacy@aduflow.net</a>. We will respond within 30 days.</p>
              </PolicySection>

              <PolicySection id="data-retention" title="8. Data Retention">
                <p className="text-sm">We retain your data for the following periods:</p>
                <ul className="text-sm space-y-2 mt-2 ml-4 list-disc">
                  <li><strong>Active Account Data:</strong> Retained for the duration of your subscription plus 90 days after cancellation.</li>
                  <li><strong>Billing Records:</strong> Retained for 7 years to comply with financial regulations.</li>
                  <li><strong>Usage Logs:</strong> Retained for 12 months for security and debugging purposes.</li>
                  <li><strong>Deleted Account Data:</strong> Permanently purged within 90 days of account deletion request.</li>
                </ul>
                <p className="text-sm mt-3">You may request early deletion of your data by contacting <a href="mailto:privacy@aduflow.net" style={{ color: '#E8732A' }}>privacy@aduflow.net</a>.</p>
              </PolicySection>

              <PolicySection id="gdpr" title="9. GDPR Compliance">
                <p className="text-sm">For users in the European Economic Area (EEA), we comply with the General Data Protection Regulation (GDPR):</p>
                <ul className="text-sm space-y-2 mt-2 ml-4 list-disc">
                  <li><strong>Legal Basis:</strong> We process data based on contract performance, legitimate interests, legal obligations, or your explicit consent.</li>
                  <li><strong>Data Controller:</strong> ADU Flow acts as the data controller for account and usage data.</li>
                  <li><strong>International Transfers:</strong> Data may be transferred to and processed in the United States. We use standard contractual clauses where required.</li>
                  <li><strong>Supervisory Authority:</strong> You have the right to lodge a complaint with your local data protection authority.</li>
                </ul>
              </PolicySection>

              <PolicySection id="contact" title="10. Contact Us">
                <p className="text-sm">For privacy-related questions, requests, or concerns, contact us:</p>
                <ul className="text-sm space-y-2 mt-3 ml-4 list-disc">
                  <li><strong>Email:</strong> <a href="mailto:privacy@aduflow.net" style={{ color: '#E8732A' }}>privacy@aduflow.net</a></li>
                  <li><strong>Website:</strong> <a href="https://aduflow.net" style={{ color: '#E8732A' }}>aduflow.net</a></li>
                </ul>
                <p className="text-sm mt-3">We may update this Privacy Policy periodically. Material changes will be communicated via email or a prominent notice on the platform. Continued use of ADU Flow after changes constitutes acceptance of the updated policy.</p>
              </PolicySection>
            </div>

            {/* Footer nav */}
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 text-sm font-medium transition-colors hover:text-orange-500"
                style={{ color: '#6B7280' }}
              >
                <ArrowLeft size={16} />
                Back to Home
              </button>
              <button
                onClick={() => navigate('/terms-of-use')}
                className="text-sm font-medium transition-colors hover:text-orange-500"
                style={{ color: '#E8732A' }}
              >
                View Terms of Use →
              </button>
            </div>
          </main>
        </div>
      </div>

      {/* Footer */}
      <footer className="mt-12 py-8 px-4 text-center" style={{ background: '#0D2137', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>&copy; 2026 ADU Flow. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
